























































































import { Component, Watch, Vue } from 'vue-property-decorator';
import TrackTableList from '@/components/track/TrackTableList.vue';
import TrackListFilter, { TrackListFilterValue } from '@/components/track/TrackListFilter.vue';
import UPagination from '@/ui-lib/pagination/Pagination.vue';
import UBlock from '@/ui-lib/block/Block.vue';
import {
  CompilationService,
  PlaylistService,
  TagService,
  TrackService,
  TrackServiceListParams
} from '@/api';
import { Route } from 'vue-router';
import { TrackListItem } from '@/types';
import { TableField } from '@/ui-lib/table/Table.vue';
import { PlaylistsSelectModalParams } from '@/components/modals/PlaylistsSelectModal.vue';
import { CompilationsSelectModalParams } from '@/components/modals/CompilationsSelectModal.vue';
import UTextField from '@/ui-lib/text-field/TextField.vue';
import UButton from '@/ui-lib/button/Button.vue';
import { RequestOptions } from '@/api/api';
import { debounce } from '@/utils/debounce';
import { TrackTagsSelectModalParams } from '@/components/modals/TrackTagsSelectModal.vue';
import { SelectMultipleValue } from '@/ui-lib/select-field/SelectField.vue';
import { tag } from '@/store/modules/tag';

const limit = 30;

// Only ids survive a reload or a shared link. Titles are loaded alongside the tracks, so the
// filter and the table change together instead of the filter flashing a bare id first.
const loadPlaylistTitles = async (
  ids: number[],
  known: Record<number, string>
): Promise<Record<number, string>> => {
  const missing = ids.filter((id) => !(id in known));

  if (!missing.length) {
    return known;
  }

  try {
    const list = await PlaylistService.listByIDs(missing);
    const titles = { ...known };
    list.forEach((pl) => (titles[pl.id] = pl.title));
    return titles;
  } catch (e) {
    // The filter falls back to the bare id, which is enough to keep filtering.
    return known;
  }
};

const getStart = (route: Route) =>
  typeof route.query.start === 'string' ? parseInt(route.query.start, 10) : 0;
const getSearch = (route: Route) =>
  typeof route.query.search === 'string' ? route.query.search : '';
const getIDList = (route: Route, key: string): number[] => {
  const val = route.query[key];

  return typeof val === 'string'
    ? val
        .split(',')
        .map((v) => parseInt(v, 10))
        .filter((v) => v > 0)
    : [];
};
const getIds = (route: Route): number[] => getIDList(route, 'ids');
const getFilter = (route: Route): TrackListFilterValue => ({
  playlistIDs: getIDList(route, 'playlistIDs'),
  tagIDs: getIDList(route, 'tagIDs'),
  compilationIDs: getIDList(route, 'compilationIDs'),
  showAds: route.query.ads === '1'
});
const getSortField = (
  route: Route
): 'id' | 'likes' | 'dislikes' | 'listeners_per_month' | 'skips_per_month' => {
  const val = route.query?.sortField?.toString();

  switch (val) {
    case 'likes':
    case 'dislikes':
    case 'listeners_per_month':
    case 'skips_per_month':
      return val;
    default:
      return 'id';
  }
};

const getSortOrder = (route: Route): 'asc' | 'desc' => {
  const val = route.query?.sortOrder?.toString();
  switch (val) {
    case 'asc':
      return val;
    default:
      return 'desc';
  }
};

const getListParams = (route: Route): TrackServiceListParams => {
  const filter = getFilter(route);

  return {
    order_column: getSortField(route),
    order_dir: getSortOrder(route),
    search: getSearch(route),
    ids: getIds(route),
    playlistIDs: filter.playlistIDs,
    tagIDs: filter.tagIDs,
    compilationsIds: filter.compilationIDs,
    // Everything except ads (status 2), unprocessed uploads included. Comparing shows the
    // exact tracks asked for: a duplicate that turned out to be an ad must not vanish.
    statuses: filter.showAds || getIds(route).length > 0 ? [] : [0, 1]
  };
};

@Component<TrackListView>({
  components: {
    UTextField,
    UButton,
    UPagination,
    UBlock,
    TrackTableList,
    TrackListFilter
  },

  async beforeRouteEnter(to, from, next) {
    try {
      const [{ list, total }, playlistTitles] = await Promise.all([
        TrackService.list({
          ...getListParams(to),
          start: getStart(to),
          limit: limit,
          withSimilar: true
        }),
        loadPlaylistTitles(getFilter(to).playlistIDs, {})
      ]);

      next((vm) => {
        vm.tracks = list;
        vm.total = total;
        vm.playlistTitles = playlistTitles;
        vm.search = getSearch(to);
      });
    } catch (e) {
      next(e);
    }
  },

  async beforeRouteUpdate(to, from, next) {
    try {
      if (this.abortList) {
        this.abortList.abort();
      }

      this.abortList = new AbortController();

      await this.updateList(to, { signal: this.abortList.signal });

      next();
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') {
        next();
      } else {
        next(e);
      }
    }
  },

  metaInfo() {
    return {
      title: getIds(this.$route).length > 0 ? 'Сравнение треков' : 'Все треки'
    };
  }
})
export default class TrackListView extends Vue {
  private total = 0;
  private tracks: TrackListItem[] = [];
  private tableFields: TableField[] = [
    {
      name: 'select',
      label: '',
      width: '38px'
    },

    {
      name: 'play-pause',
      label: '',
      width: '40px'
    },

    {
      name: 'id',
      label: 'ID',
      sortable: true,
      width: '65px'
    },

    {
      name: 'title',
      label: 'Название'
    },

    {
      name: 'compilations',
      label: 'Подборки',
      width: '200px'
    },

    {
      name: 'playlists',
      label: 'Плейлисты',
      width: '200px'
    },

    {
      name: 'tags',
      label: 'Теги',
      width: '200px'
    },

    {
      name: 'likes',
      label: 'Лайки',
      sortable: true,
      width: '60px',
      align: 'right'
    },

    {
      name: 'dislikes',
      label: 'Дизлайки',
      sortable: true,
      width: '60px',
      align: 'right'
    },

    {
      name: 'listeners_per_month',
      label: 'Слуш/мес',
      sortable: true,
      width: '60px',
      align: 'right'
    },

    {
      name: 'skips_per_month',
      label: 'Скип/мес',
      sortable: true,
      width: '60px',
      align: 'right'
    }
  ];
  private selected: number[] = [];
  private isLoading = false;
  private search = '';
  private abortList: null | AbortController = null;
  private selectFoundLoading = false;
  private playlistTitles: Record<number, string> = {};
  // Ids behind the last «select all found», kept to hide the offer once they are all selected.
  private foundIDs: number[] = [];

  @Watch('search')
  private searchChanged = debounce((val: string) => {
    this.$router.replace({
      query: {
        ...this.$route.query,
        start: undefined,
        ids: undefined,
        search: val ? val : undefined
      }
    });
  }, 300);

  @Watch('$route')
  private routeChanged(val: Route, oldVal: Route) {
    if (!('search' in val.query)) {
      this.search = '';
    }

    if (this.listKey(val) !== this.listKey(oldVal)) {
      this.foundIDs = [];
    }
  }

  // Paging alone keeps the same set of found tracks.
  private listKey(route: Route): string {
    return JSON.stringify(getListParams(route));
  }

  private get filter(): TrackListFilterValue {
    return getFilter(this.$route);
  }

  private get isFiltered(): boolean {
    const filter = this.filter;

    return (
      getSearch(this.$route) !== '' ||
      filter.playlistIDs.length > 0 ||
      filter.tagIDs.length > 0 ||
      filter.compilationIDs.length > 0
    );
  }

  // Offered only for a narrowed list: one click on the bare catalogue would take every track,
  // and a list that fits one page is covered by the header checkbox.
  private get isSelectFoundOffered(): boolean {
    if (!this.isFiltered || this.total <= this.tracks.length) {
      return false;
    }

    const selected = new Set(this.selected);

    return !(this.foundIDs.length > 0 && this.foundIDs.every((id) => selected.has(id)));
  }

  private changeFilter(filter: TrackListFilterValue) {
    // Applying a picker untouched would be a duplicate navigation, which vue-router rejects.
    if (JSON.stringify(filter) === JSON.stringify(this.filter)) {
      return;
    }

    const join = (ids: number[]) => (ids.length ? ids.join(',') : undefined);

    this.$router.push({
      query: {
        ...this.$route.query,
        start: undefined,
        ids: undefined,
        playlistIDs: join(filter.playlistIDs),
        tagIDs: join(filter.tagIDs),
        compilationIDs: join(filter.compilationIDs),
        ads: filter.showAds ? '1' : undefined
      }
    });
  }

  private async selectFound() {
    const route = this.$route;

    this.selectFoundLoading = true;

    try {
      // Only regular tracks: a share link rejects ads and unprocessed ones as a whole.
      const { ids } = await TrackService.listIDs({ ...getListParams(route), statuses: [1] });

      if (this.listKey(this.$route) !== this.listKey(route)) {
        return;
      }

      const selected = new Set(this.selected);
      this.selected.push(...ids.filter((id) => !selected.has(id)));

      this.foundIDs = ids;

      const skipped = this.total - ids.length;

      if (skipped > 0) {
        this.$notify({
          type: 'info',
          title: `Выбрано треков: ${ids.length}`,
          text: `Ещё ${skipped} не выбраны: это реклама или необработанные треки, в ссылку они не попадут`
        });
      }
    } catch (e) {
      this.$notify({
        type: 'error',
        title: 'Ошибка',
        text: e?.message || 'Неизвестная ошибка'
      });
    } finally {
      this.selectFoundLoading = false;
    }
  }

  private get limit() {
    return limit;
  }

  private get start() {
    return getStart(this.$route);
  }

  private get isComparing() {
    return getIds(this.$route).length > 0;
  }

  private stopComparing() {
    this.$router.push({
      query: {
        ...this.$route.query,
        start: undefined,
        ids: undefined
      }
    });
  }

  private changeStart(newStart: number) {
    this.$router.push({
      query: {
        ...this.$route.query,
        start: newStart.toString()
      }
    });
  }

  private async updateList(route: Route, opts?: RequestOptions) {
    const [{ list, total }, playlistTitles] = await Promise.all([
      TrackService.list(
        {
          ...getListParams(route),
          start: getStart(route),
          limit: limit,
          withSimilar: true
        },
        opts
      ),
      loadPlaylistTitles(getFilter(route).playlistIDs, this.playlistTitles)
    ]);

    this.tracks = list;
    this.total = total;
    this.playlistTitles = playlistTitles;
  }

  private selectPlaylists() {
    const playlistIDs: number[] = [];

    const params: PlaylistsSelectModalParams = {
      selected: playlistIDs,
      onSelect: async () => {
        if (playlistIDs.length === 0) {
          this.$notify({
            type: 'info',
            title: 'Нет изменений'
          });
          return;
        }

        this.isLoading = true;

        try {
          await PlaylistService.addTracks(playlistIDs, this.selected);

          await this.updateList(this.$route);
          // this.selected.splice(0)

          this.$notify({
            type: 'success',
            title: 'Треки обновлены'
          });
        } catch (e) {
          this.$notify({
            type: 'error',
            title: 'Ошибка',
            text: e?.message || 'Неизвестная ошибка'
          });
        }

        this.isLoading = false;
      }
    };

    this.$modal.show('playlists-select', params);
  }

  private selectCompilations() {
    const compilationIDs: number[] = [];

    const params: CompilationsSelectModalParams = {
      selected: compilationIDs,
      onSelect: async () => {
        if (compilationIDs.length === 0) {
          this.$notify({
            type: 'info',
            title: 'Нет изменений'
          });
          return;
        }

        this.isLoading = true;

        try {
          await CompilationService.addTracks(compilationIDs, this.selected);

          await this.updateList(this.$route);
          // this.selected.splice(0)

          this.$notify({
            type: 'success',
            title: 'Треки обновлены'
          });
        } catch (e) {
          this.$notify({
            type: 'error',
            title: 'Ошибка',
            text: e?.message || 'Неизвестная ошибка'
          });
        }

        this.isLoading = false;
      }
    };

    this.$modal.show('compilations-select', params);
  }

  private selectTags() {
    const params: TrackTagsSelectModalParams = {
      trackIds: this.selected,
      onSelect: async (value: SelectMultipleValue) => {
        const tags: { name: string }[] = [];

        if (value) {
          if (Array.isArray(value)) {
            tags.push(...value.map((v) => ({ name: v.label })));
          } else {
            tags.push({ name: value.label });
          }
        }

        if (tags.length === 0 || this.selected.length === 0) {
          this.$notify({ type: 'info', title: 'Нет изменений' });
          return;
        }

        await TagService.attachTracks(this.selected, tags);

        // Массовое проставление может завести новый тег: без обновления
        // справочника он не попадёт в подсказки и будет создан повторно.
        await tag.context(this.$store).actions.loadList();
        await this.updateList(this.$route);

        this.$notify({
          type: 'success',
          title: 'Треки обновлены'
        });
      }
    };

    this.$modal.show('track-tags-select', params);
  }
}
